import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {PopoverIconComponent} from '@brunata-angular/ui';
import {TRANSLATE, TranslatePipe} from '@brunata-angular/util';
import {
    ActionButtonComponent,
    ActionLabel,
} from '../../shared/ui/action-button/action-button.component';
import {
    SftpConnectionTestProgress,
    SftpConnectionTestStage,
    sftpConnectionTestStages,
} from '../sftp-connection.model';

type RowStatus = 'pending' | 'active' | 'done' | 'failed';

type RailRow = {
    stage: SftpConnectionTestStage;
    title: string;
    detail: string | null;
    status: RowStatus;
    detailClass: string;
};

const stageKeys: Record<SftpConnectionTestStage, string> = {
    connect: 'sftpconnection_modal_test_stage_connect',
    login: 'sftpconnection_modal_test_stage_login',
    sftp: 'sftpconnection_modal_test_stage_sftp',
    write: 'sftpconnection_modal_test_stage_write',
};

const rowStatus = (
    progress: SftpConnectionTestProgress | null,
    stage: SftpConnectionTestStage,
    current: SftpConnectionTestStage | undefined,
    running: boolean,
): RowStatus => {
    if (progress === null || progress.details[stage] === undefined) {
        return 'pending';
    }
    if (stage !== current) {
        return 'done';
    }
    switch (progress.state) {
        case 'running':
            return running ? 'active' : 'failed';
        case 'failed':
            return 'failed';
        case 'succeeded':
            return 'done';
    }
};

const detailClass = (status: RowStatus, outcome: boolean): string => {
    if (status === 'failed') {
        return 'small text-danger';
    }
    return outcome ? 'small text-success fw-bold' : 'small text-secondary';
};

@Component({
    selector: 'app-sftp-connection-test',
    standalone: true,
    host: {class: 'card'},
    imports: [TranslatePipe, ActionButtonComponent, PopoverIconComponent],
    template: `
        <div class="card-header bg-white d-flex align-items-center gap-2">
            <span>{{ 'sftpconnection_modal_test_title' | translate: 'online' : 'export' }}</span>
            <app-popover-icon
                [popover]="
                    'sftpconnection_modal_test_description' | translate: 'online' : 'export'
                " />
            <app-action-button
                class="ms-auto"
                variant="outline-primary"
                icon="bi-send"
                [label]="runLabel"
                [busy]="running()"
                [disabled]="disabled()"
                (run)="testRequested.emit()" />
        </div>
        <div class="card-body bg-primary-faint overflow-auto">
            <ol class="rail list-unstyled mb-0" aria-live="polite">
                @for (row of rows(); track row.stage) {
                    <li [class]="'rail-' + row.status">
                        @if (row.status === 'active') {
                            <span
                                class="spinner-border spinner-border-sm text-primary"
                                role="status"></span>
                        }
                        <div>{{ row.title }}</div>
                        @if (row.detail !== null) {
                            <div [class]="'detail ' + row.detailClass">{{ row.detail }}</div>
                        }
                    </li>
                }
            </ol>
        </div>
    `,
    styles: `
        .card-body {
            min-height: 13rem;
        }

        .rail li {
            position: relative;
            padding: 0 0 0.75rem 1.5rem;
        }

        .rail li::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0.4rem;
            width: 0.6875rem;
            height: 0.6875rem;
            border-radius: 50%;
            background-color: var(--bs-body-bg);
            border: 2px solid var(--bs-border-color);
        }

        .rail li::after {
            content: '';
            position: absolute;
            left: 0.3125rem;
            top: 1.15rem;
            bottom: 0;
            width: 1px;
            background-color: var(--bs-border-color);
            transform-origin: top;
        }

        .rail li:last-child::after {
            display: none;
        }

        .rail li.rail-pending {
            color: var(--bs-secondary-color);
        }

        .rail li.rail-active::before {
            display: none;
        }

        .rail li.rail-done::before {
            background-color: var(--bs-success);
            border-color: var(--bs-success);
            animation: dot-pop 0.3s ease-out both;
        }

        .rail li.rail-done::after {
            background-color: var(--bs-success);
            animation: line-draw 0.3s ease-out both;
        }

        .rail li.rail-failed::before {
            background-color: var(--bs-danger);
            border-color: var(--bs-danger);
            animation: dot-pop 0.3s ease-out both;
        }

        .rail .spinner-border {
            position: absolute;
            left: -0.15625rem;
            top: 0.25rem;
        }

        .rail .detail {
            animation: rail-in 0.25s ease-out both;
        }

        @keyframes rail-in {
            from {
                opacity: 0;
                transform: translateY(-0.25rem);
            }
        }

        @keyframes dot-pop {
            from {
                transform: scale(0.4);
            }
            60% {
                transform: scale(1.25);
            }
        }

        @keyframes line-draw {
            from {
                transform: scaleY(0);
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .rail li::before,
            .rail li::after,
            .rail .detail {
                animation: none;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SftpConnectionTestComponent {
    readonly progress = input.required<SftpConnectionTestProgress | null>();
    readonly running = input.required<boolean>();
    readonly disabled = input.required<boolean>();

    readonly testRequested = output();

    protected readonly runLabel: ActionLabel = {
        key: 'sftpconnection_modal_test_run',
        module: 'export',
    };

    private readonly translate = inject(TRANSLATE);

    protected readonly rows = computed<ReadonlyArray<RailRow>>(() => {
        const progress = this.progress();
        const current = sftpConnectionTestStages
            .filter(stage => progress?.details[stage] !== undefined)
            .at(-1);
        return sftpConnectionTestStages.map(stage => {
            const status = rowStatus(progress, stage, current, this.running());
            const outcome = progress?.state === 'succeeded' && stage === current;
            return {
                stage,
                title: this.translate(stageKeys[stage], 'online', 'export'),
                detail: progress?.details[stage] ?? null,
                status,
                detailClass: detailClass(status, outcome),
            };
        });
    });
}
